// online.{brand} dashboard. Reads claims from /api/auth/me (via RequireAuth),
// and refreshes the depot list against the adapter. Mirrors the Hono worker's
// routes/online.tsx authenticated branch.

import { useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { authClient } from "../lib/auth";
import { IdentityHeader } from "../lib/IdentityHeader";
import { brandLabel, resolveBrand, surfaceUrl } from "../lib/brand";
import type { SessionView } from "../lib/session";

interface DepotItem {
  depotKey: string;
  primary?: boolean;
}

// Read the live depot list from the adapter's session-authed endpoint, proxied
// verbatim by the Worker (online.{brand}/api/* → adapter). The adapter resolves
// the session's `sub` → CORE userId server-side and queries CORE, so it reflects
// a freshly-reassigned async-signup depot the JWT didn't carry yet.
//
// Why this path and not the Worker's /api/depots: that one mints a corp service
// token and calls the IDENTITY_SERVER-gated /v1/sessions/{sub}/depots, which (a)
// needs the svc-token audience added to api-ingress, and (b) reads the WORKER
// session — never set on the real plane, where auth lives in the adapter's
// __Host-mf_session. /auth/me/depots sits on the FREE_ACCESS /auth/** surface,
// authenticates via that same cookie, and takes `sub` from the session (no IDOR).
// Returns a bare DepotDto[] array.
async function fetchDepots(): Promise<DepotItem[]> {
  const res = await fetch(`/api/mf-auth-adapter/auth/me/depots`, {
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`depots_load_failed_${res.status}`);
  return (await res.json()) as DepotItem[];
}

const MAX_POLLS = 16; // ~16 × 2.5s ≈ 40s

export interface DashboardProps {
  session: SessionView;
}

export function Dashboard({ session }: DashboardProps) {
  const attempts = useRef(0);
  const brand = resolveBrand();
  const [addOpen, setAddOpen] = useState(false);
  const [pinHelp, setPinHelp] = useState(false);

  // "I already have a depot" → full-page B2C HUB_LINKDEPOT (CustomerID + PIN). On
  // success the adapter links that depot onto THIS session and 302s back here,
  // where the depot list refetches and shows the merged set. loginRedirect
  // navigates then throws by contract, so swallow the post-navigation throw.
  const linkExistingDepot = () => {
    try {
      authClient.loginRedirect({
        brand,
        flow: "linkdepot",
        return: surfaceUrl("online", brand) + "/",
      });
    } catch {
      /* navigation already initiated */
    }
  };

  const depots = useQuery({
    queryKey: ["depots", session.sub],
    queryFn: async () => {
      attempts.current += 1;
      return fetchDepots();
    },
    // Fast-path: hydrate with the JWT-embedded depots so the page renders
    // before the adapter responds. Same idea as the worker's "fast-path"
    // render in routes/online.tsx.
    initialData: (session.depots ?? []).map(
      (depotKey): DepotItem => ({
        depotKey,
        primary: depotKey === session.primaryDepot,
      }),
    ),
    retry: false,
    // Async signup provisions the CORE user + reassigns a real pool depot in the
    // background (~10-20s), so the depot row appears a few seconds after landing.
    // Poll until a depot shows or we hit the cap, then stop.
    refetchInterval: (query) => {
      const data = query.state.data as DepotItem[] | undefined;
      if (data && data.length > 0) return false; // depot arrived — done
      if (attempts.current >= MAX_POLLS) return false; // give up after ~40s
      return 2500;
    },
  });

  return (
    <>
      <IdentityHeader email={session.email} sub={session.sub} />
      <header className="hub-page-header">
        <h1>{brandLabel(session.brand)} dashboard</h1>
        <p>
          Depots load from the adapter; the JWT-embedded list shows instantly
          while the request is in flight.
        </p>
      </header>
      <section className="hub-explainer" data-testid="depots-section">
        <h2>Your depots</h2>
        {depots.data.length === 0 ? (
          <p style={{ margin: 0 }} data-hub-depots-empty>
            {depots.isFetching
              ? "Loading your depots…"
              : "No depots on file."}
          </p>
        ) : (
          <ul className="hub-qa-list" data-hub-depots-list>
            {depots.data.map((d) => (
              <li key={d.depotKey}>
                <code>{d.depotKey}</code>
                {d.primary ? <strong> (primary)</strong> : null}
              </li>
            ))}
          </ul>
        )}
        {depots.data.length > 0 ? (
          <p
            className="muted"
            style={{ margin: "10px 0 0", fontSize: 13 }}
            data-hub-depots-note
          >
            Demo: the depot above is a <strong>real DEV depot</strong> reassigned
            from a small pool on signup — opening a brand-new depot runs behind the
            signed-contract onboarding pipeline, so the PoC recycles existing ones.
          </p>
        ) : null}
        {depots.isError ? (
          <p style={{ margin: "8px 0 0", color: "var(--hub-danger)" }}>
            Could not refresh from the adapter — showing the last known list.
          </p>
        ) : null}
      </section>
      <main className="hub-cta-row">
        <button
          type="button"
          className="hub-cta-button"
          data-hub-add-depot
          onClick={() => setAddOpen((v) => !v)}
        >
          Add another depot
        </button>
        <Link to="/devices" className="hub-cta-button secondary">
          Manage 2FA devices
        </Link>
        <button
          type="button"
          className="hub-cta-button secondary"
          data-hub-signout
          onClick={() => {
            // authClient.logout() POSTs /auth/logout then navigates to the B2C
            // end-session URL. If the adapter is unreachable, bounce to the
            // neutral product home (altersvorsorge.{brand}) so the signed-in
            // surface doesn't linger.
            authClient.logout().catch(() => {
              window.location.href =
                surfaceUrl("altersvorsorge", resolveBrand()) + "/";
            });
          }}
        >
          Sign out
        </button>
      </main>
      {addOpen ? (
        <section className="hub-explainer" data-hub-add-depot-panel>
          <h2>Add another depot</h2>
          <p className="muted" style={{ margin: "0 0 14px" }}>
            You can hold more than one depot. Link a depot you already have, or
            open a brand-new one.
          </p>
          <div className="hub-cta-row">
            <button
              type="button"
              className="hub-cta-button"
              data-hub-linkdepot
              onClick={linkExistingDepot}
            >
              I have a depot — link with Customer ID + PIN
            </button>
            <a
              className="hub-cta-button secondary"
              href={surfaceUrl("altersvorsorge", brand) + "/"}
              data-hub-newdepot
            >
              Open a new depot
            </a>
            <button
              type="button"
              className="hub-cta-button secondary"
              onClick={() => setPinHelp(true)}
            >
              Don&apos;t know your PIN?
            </button>
          </div>
          {pinHelp ? (
            <p className="muted" style={{ margin: "12px 0 0", fontSize: 13 }}>
              Recovering a forgotten PIN (the Online-PIN request) is{" "}
              <strong>out of scope</strong> for this demo — in production
              you&apos;d request a fresh PIN by post or email.
            </p>
          ) : null}
        </section>
      ) : null}
    </>
  );
}
